import { apiClient } from './client';
import { 
  HuntStatus, 
  ParticipantRole, 
  ParticipantSummary,
} from '@gps-hunt/shared';

// Response types for hunt endpoints
export interface CreateHuntResponse {
  huntId: string;
  joinCode: string;
  status: HuntStatus;
  createdAt: string;
}

export interface JoinHuntResponse {
  huntId: string;
  joinCode: string;
  status: HuntStatus;
  role: ParticipantRole;
}

export interface GetHuntResponse {
  hunt: {
    id: string;
    joinCode: string;
    status: HuntStatus;
    createdBy: string;
    pinOrder: string[];
    hidersReady: string[];
    completedPinIds: string[];
    createdAt: string;
    startedAt?: string;
    completedAt?: string;
    name: string;
    description?: string;
    creatorDisplayName: string;
  };
  participants: ParticipantSummary[];
  pins: unknown[];
  myRole: ParticipantRole;
  progress?: {
    currentPinIndex: number;
    completedPinIds: string[];
    itemsFoundAtCurrentPin: number;
  };
}

export interface LeaveHuntResponse {
  message: string;
  huntDeleted: boolean;
}

export interface HuntSummary {
  id: string;
  name: string;
  description?: string;
  joinCode: string;
  status: HuntStatus;
  participantCount: number;
  pinCount: number;
  itemCount: number;
  challengeCount: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  myRole?: string;
  creatorDisplayName?: string;
}

export interface GetMyHuntsResponse {
  hunts: HuntSummary[]; // Backward compatibility
  createdHunts: HuntSummary[];
  joinedHunts: HuntSummary[];
}

export interface HuntPreviewResponse {
  name: string;
  description?: string;
  creatorDisplayName: string;
  status: HuntStatus;
  participantCount: number;
}

export interface DeleteHuntResponse {
  message: string;
  huntId: string;
}

/**
 * Hunt API service
 */
export const huntService = {
  /**
   * Create a new hunt
   */
  async createHunt(name: string, description: string | undefined, displayName: string): Promise<CreateHuntResponse> {
    const response = await apiClient.post<CreateHuntResponse>('/hunts', {
      name,
      description,
      displayName,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create hunt');
    }

    return response.data;
  },

  /**
   * Get hunt preview info before joining
   */
  async getHuntPreview(joinCode: string): Promise<HuntPreviewResponse> {
    const response = await apiClient.get<HuntPreviewResponse>(`/hunts/preview/${joinCode}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Invalid join code');
    }

    return response.data;
  },

  /**
   * Join an existing hunt by code
   */
  async joinHunt(
    joinCode: string, 
    displayName: string, 
    role: ParticipantRole
  ): Promise<JoinHuntResponse> {
    const response = await apiClient.post<JoinHuntResponse>('/hunts/join', {
      joinCode,
      displayName,
      role,
    });

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to join hunt');
    }

    return response.data;
  },

  /**
   * Get hunt details
   */
  async getHunt(huntId: string): Promise<GetHuntResponse> {
    const response = await apiClient.get<GetHuntResponse>(`/hunts/${huntId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get hunt');
    }

    return response.data;
  },

  /**
   * Leave a hunt
   */
  async leaveHunt(huntId: string): Promise<LeaveHuntResponse> {
    const response = await apiClient.post<LeaveHuntResponse>(`/hunts/${huntId}/leave`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to leave hunt');
    }

    return response.data;
  },

  /**
   * Kick a participant from a hunt (creator only)
   * Requires a justification reason
   */
  async kickParticipant(huntId: string, userId: string, justification: string): Promise<{ message: string; removedUserId: string }> {
    const response = await apiClient.delete<{ message: string; removedUserId: string }>(
      `/hunts/${huntId}/participants/${userId}`,
      { justification }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to remove participant');
    }

    return response.data;
  },

  /**
   * Start a hunt (transition from STARTING to ACTIVE after countdown)
   */
  async startHunt(huntId: string): Promise<{ huntId: string; status: string; startedAt: string }> {
    const response = await apiClient.post<{ huntId: string; status: string; startedAt: string }>(
      `/hunts/${huntId}/start`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to start hunt');
    }

    return response.data;
  },

  /**
   * Cancel the starting countdown (any participant can abort)
   */
  async cancelStart(huntId: string): Promise<{ huntId: string; newStatus: string; message: string }> {
    const response = await apiClient.post<{ huntId: string; newStatus: string; message: string }>(
      `/hunts/${huntId}/cancel-start`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to cancel start');
    }

    return response.data;
  },

  /**
   * Get all hunts created by the current user
   */
  async getMyHunts(): Promise<GetMyHuntsResponse> {
    const response = await apiClient.get<GetMyHuntsResponse>('/users/me/hunts');

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get your hunts');
    }

    return response.data;
  },

  /**
   * Delete a hunt (creator only)
   */
  async deleteHunt(huntId: string): Promise<DeleteHuntResponse> {
    const response = await apiClient.delete<DeleteHuntResponse>(`/hunts/${huntId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to delete hunt');
    }

    return response.data;
  },
};
